import SDHeader from "./SDHeader";
import LeftNav from "../LeftNav";
import SearchOverlay from "../SearchOverlay";
import RightNav from "../RightNav";
import DocumentList from "../DocumentList";
import "./SubjectDocuments.css";
import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";

import NotFound from "../NotFound";
import { jwtDecode } from "jwt-decode";
import GradePopover from "../GradePopover";

import {
  useFloating, //figures out position
  offset, //adds space between button and popup
  flip, //moves popup to another side if there’s no space
  shift, //nudges popup back inside the screen
  autoUpdate, //keeps it stuck to the button when things change
  useClick,
  useDismiss,
  useRole,
  useInteractions,
} from "@floating-ui/react";

import Popover from "../Popover";

const SubjectDocuments = ({ isAuthenticated, setAuth }) => {
  const { id } = useParams();

  const [databaseNames, setDatabaseNames] = useState([]);
  const [storage_path, setStoragePath] = useState([]);
  const [file_rating, setFileRating] = useState([]);
  const [fileIds, setFileId] = useState([]);
  const [showUploadIcon, setShowUploadIcon] = useState(false);

  const [notFound, setNotFound] = useState(false);

  const [leftNavOpen, setLeftNavOpen] = useState(false);
  const [rightNavOpen, setRightNavOpen] = useState(false);
  const [searchActive, setSearchActive] = useState(false);

  const searchInputRef = useRef(null);
  const leftNavRef = useRef(null);
  const rightNavRef = useRef(null);

  const openSearch = () => setSearchActive(true);
  const closeSearch = () => setSearchActive(false);

  const openLeftNav = () => setLeftNavOpen(true);
  const closeLeftNav = () => setLeftNavOpen(false);

  const openRightNav = () => setRightNavOpen(true);
  const closeRightNav = () => setRightNavOpen(false);

  const [activeFileId, setActiveFileId] = useState(null);

  const [ratingTrigger, setRatingTrigger] = useState(0);

  const [selectedGradeRange, setSelectedGradeRange] = useState("");
  const [open, setOpen] = useState(false);
  const [referenceEl, setReferenceEl] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    document.title = "Share2Teach";
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      const userRole = decoded.role;

      const validRoles = ["admin", "moderator", "educator"];
      if (!validRoles.includes(userRole)) return;
      setShowUploadIcon(true);
    }
  }, []);

  const handleRatingSubmitted = () => {
    setRatingTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    // 🧠 skip fetching all files if user has already chosen a grade
    if (!selectedGradeRange) return;

    const delay = setTimeout(() => {
      const fetchFilteredFiles = async () => {
        try {
          console.log("Fetching filtered files...");
          const res = await fetch(
            `${API_URL}/api/files/by-subject-grade/${id}/${selectedGradeRange}`
          );
          const files = await res.json();

          setDatabaseNames(files.map((d) => d.fileName));
          setStoragePath(files.map((d) => d.storagePath));
          setFileRating(files.map((d) => d.fileRating));
          setFileId(files.map((d) => d.id)); // ✅ use "id" instead of "fileId"

          console.log("Fetched filtered files:", files);

          if (files.length === 0) setNotFound(true);
          else setNotFound(false);
        } catch (err) {
          console.error("Error fetching filtered files:", err);
        }
      };

      fetchFilteredFiles();
    }, 300);

    return () => clearTimeout(delay);
  }, [selectedGradeRange, id]);

  useEffect(() => {
    const validIds = [
      "science",
      "mathematics",
      "programming",
      "arts",
      "history",
      "chemistry",
      "management",
      "design",
      "psychology",
      "finance",
    ];
    if (!validIds.includes(id)) return;

    const fetchFiles = async () => {
      try {
        console.log("Fetching all files for subject:", id);
        const res = await fetch(`${API_URL}/api/files/${id}`);
        setDatabaseNames(await res.json());

        const res2 = await fetch(`${API_URL}/api/links/${id}`);
        setStoragePath(await res2.json());

        const res3 = await fetch(`${API_URL}/api/ratings/${id}`);
        setFileRating(await res3.json());

        const res4 = await fetch(`${API_URL}/api/ids/${id}`);
        setFileId(await res4.json());
      } catch (error) {
        console.error("Error fetching data", error);
      }
    };

    fetchFiles();
  }, [id, ratingTrigger, selectedGradeRange]);

  const onRatingSubmitted = () => {
    setRatingTrigger((prev) => prev + 1);
  };

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "left",
    middleware: [offset(-5), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  // Interactions
  const click = useClick(context);
  const dismiss = useDismiss(context); // ✅ click outside
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  useEffect(() => {
    refs.setReference(referenceEl); //Okay, position the popover relative to this element
  }, [referenceEl]);

  return (
    <div className="sub-parent">
      <div>
        <section className="subject-container">
          {!searchActive && (
            <>
              <SDHeader
                showSearchLogo={true}
                leftNavOpen={leftNavOpen}
                rightNavOpen={rightNavOpen}
                openLeftNav={openLeftNav}
                openSearch={openSearch}
                openRightNav={openRightNav}
                closeRightNav={closeRightNav}
                showPCSearch={true}
                showUploadIcon={showUploadIcon}
              />

              <LeftNav
                leftNavOpen={leftNavOpen}
                closeLeftNav={closeLeftNav}
                leftNavRef={leftNavRef}
                isAuthenticated={isAuthenticated}
                setAuth={setAuth}
              />

              <RightNav
                rightNavRef={rightNavRef}
                closeRightNav={closeRightNav}
                rightNavOpen={rightNavOpen}
                activeFileId={activeFileId}
                onRatingSubmitted={onRatingSubmitted} // <-- pass callback
                isAuthenticated={isAuthenticated}
              />
              <div className="g-popover">
                <GradePopover
                  onSelect={(grade) => setSelectedGradeRange(grade)} //pass a method as a prop
                />
              </div>

              {open && (
                <Popover
                  ref={refs.setFloating}
                  style={floatingStyles}
                  fileId={activeFileId}
                  onRatingSubmitted={handleRatingSubmitted}
                />
              )}
              <main className="docs-container">
                {!notFound && (
                  <DocumentList
                    openRightNav={openRightNav}
                    databaseNames={databaseNames}
                    storage_path={storage_path}
                    file_rating={file_rating}
                    rightNavOpen={rightNavOpen}
                    leftNavOpen={leftNavOpen}
                    closeRightNav={closeRightNav}
                    closeLeftNav={closeLeftNav}
                    fileIds={fileIds} // 👈 must come from backend fetch
                    setActiveFileId={setActiveFileId}
                    onThreeDotClick={(e, fileId) => {
                      setReferenceEl(e.currentTarget);
                      setActiveFileId(fileId);
                      setOpen(true);
                    }}
                  />
                )}
              </main>
            </>
          )}

          {notFound && (
            <>
              <NotFound />
            </>
          )}

          <SearchOverlay
            searchActive={searchActive}
            closeSearch={closeSearch}
            searchInputRef={searchInputRef}
            openRightNav={openRightNav}
            rightNavOpen={rightNavOpen}
            leftNavOpen={leftNavOpen}
            closeRightNav={closeRightNav}
            closeLeftNav={closeLeftNav}
            setActiveFileId={setActiveFileId}
            rightNavRef={rightNavRef}
            onRatingSubmitted={onRatingSubmitted} // <-- pass callback
            ratingTrigger={ratingTrigger}
            activeFileId={activeFileId}
          />
        </section>

        <div className="subjectDocumentsFooter">
          <p>&copy; 2025 Share2Teach</p>
        </div>
      </div>
    </div>
  );
};
export default SubjectDocuments;
